import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import { RowActions } from "@/shared/ui/RowActions";
import type { PaymentTermsTemplateDTO } from "../../domain/contracts";

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  quotation: "ໃບສະເໜີລາຄາ",
  invoice: "ໃບເກັບເງິນ",
  all: "ທຸກປະເພດ",
};

const TAX_VARIANT_LABELS: Record<string, string> = {
  without_tax: "ບໍ່ມີພາສີ",
  with_tax: "ມີພາສີ",
};

type PaymentTermsTemplateTableProps = {
  data: PaymentTermsTemplateDTO[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (row: PaymentTermsTemplateDTO) => void;
  onDelete: (id: string) => void;
};

export function PaymentTermsTemplateTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDelete,
}: PaymentTermsTemplateTableProps) {
  const columns: TanstackReactTable.ColumnDef<PaymentTermsTemplateDTO>[] = [
    createSortableColumn("name", "ຊື່ແມ່ແບບ"),
    {
      id: "documentType",
      header: "ປະເພດເອກະສານ",
      cell: ({ row }) =>
        DOCUMENT_TYPE_LABELS[row.original.documentType] ??
        row.original.documentType,
    },
    {
      id: "taxVariant",
      header: "ພາສີ",
      cell: ({ row }) =>
        TAX_VARIANT_LABELS[row.original.taxVariant] ??
        row.original.taxVariant,
    },
    {
      id: "isDefault",
      header: "ເລີ່ມຕົ້ນ",
      cell: ({ row }) =>
        row.original.isDefault ? (
          <Badge variant="default">ເລີ່ມຕົ້ນ</Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const active = row.original.status === "active";
        return (
          <Badge variant={active ? "default" : "secondary"}>
            {active ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ແກ້ໄຂ",
              icon: <EditIcon className="h-4 w-4" />,
              onClick: () => onEdit(row.original),
            },
            {
              label: "ລຶບ",
              icon: <TrashIcon className="h-4 w-4" />,
              variant: "destructive",
              onClick: async () => {
                const ok = await confirm({
                  title: "ລຶບແມ່ແບບ",
                  description: "ແມ່ແບບນີ້ຈະຖືກລຶບ — ເອກະສານທີ່ບັນທຶກໄວ້ແລ້ວບໍ່ກະທົບ",
                  actionText: "ລຶບ",
                  ActionProps: { variant: "destructive" },
                });
                if (ok) onDelete(row.original.id);
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable<PaymentTermsTemplateDTO, unknown>
      noDataMessage="ບໍ່ພົບແມ່ແບບ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
